import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Search, MessageCircle, CalendarClock } from 'lucide-react';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { ActionButton, Button } from '../../components/UI';

interface AvatarItem {
  _id: string;
  name: string;
  slug: string;
  photoUrl?: string;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
}

const AdminAvatars: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const [avatars, setAvatars] = useState<AvatarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAvatars = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50', ...(searchTerm && { search: searchTerm }) });
      const response = await axios.get(`/avatars?${params}`);
      setAvatars(response.data.avatars);
    } catch (error) {
      console.error('Erro ao carregar avatares:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchAvatars();
  }, [fetchAvatars]);

  const handleDelete = async (avatar: AvatarItem) => {
    if (avatar.usageCount > 0) {
      notifications.showWarning(
        t('admin.avatarInUseTitle'),
        t('admin.avatarInUseMsg', { count: avatar.usageCount })
      );
      return;
    }
    if (!(await confirm({ message: t('admin.avatarDeleteConfirm', { name: avatar.name }), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/avatars/${avatar._id}`);
      setAvatars((prev) => prev.filter((a) => a._id !== avatar._id));
      notifications.showSuccess(t('admin.avatarDeleted'), t('admin.avatarDeletedMsg'));
    } catch (error: any) {
      console.error('Erro ao deletar avatar:', error);
      notifications.showError(t('admin.avatarDeleteError'), error.response?.data?.message || t('admin.avatarTryAgain'));
    }
  };

  // Ações da linha: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderActions = (avatar: (typeof avatars)[number]) => (
    <div className="row-actions md:justify-end">
      <ActionButton
        icon={CalendarClock}
        label="Agendamento"
        variant="primary"
        to={`/admin/agendamentos/new?avatar=${avatar._id}`}
      />
      <ActionButton
        action="edit"
        to={`/admin/avatars/${avatar._id}/edit`}
      />
      <ActionButton
        action="delete"
        onClick={() => handleDelete(avatar)}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.avatars')}</h1>
              <p className="text-gray-600">{t('admin.avatarsDesc')}</p>
            </div>
            <Button className="self-start" to="/admin/avatars/new">
              <Plus className="w-5 h-5" />
              <span>{t('admin.avatarNew')}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('common.search') as string}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="field pl-10 pr-4"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : avatars.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.avatarsEmpty')}</h3>
              <Button to="/admin/avatars/new">
                {t('admin.avatarNew')}
              </Button>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.avatarColAvatar')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.avatarColStatus')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.avatarColUsage')}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {avatars.map((avatar) => (
                    <tr key={avatar._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {avatar.photoUrl ? (
                            <img className="h-10 w-10 rounded-full object-cover" src={avatar.photoUrl} alt={avatar.name} />
                          ) : (
                            <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-xs">
                              {avatar.name.slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{avatar.name}</div>
                            <div className="text-sm text-gray-500">/a/{avatar.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${avatar.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {avatar.isActive ? t('admin.active') : t('admin.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {t('admin.avatarUsageCount', { count: avatar.usageCount })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {renderActions(avatar)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-200">
              {avatars.map((avatar) => (
                <li key={avatar._id} className="p-4 space-y-3">
                  <div className="min-w-0">
                    <div className="flex items-center">
                      {avatar.photoUrl ? (
                        <img className="h-10 w-10 rounded-full object-cover" src={avatar.photoUrl} alt={avatar.name} />
                      ) : (
                        <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-xs">
                          {avatar.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{avatar.name}</div>
                        <div className="text-sm text-gray-500">/a/{avatar.slug}</div>
                      </div>
                    </div>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">{t('admin.avatarColStatus')}</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${avatar.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {avatar.isActive ? t('admin.active') : t('admin.inactive')}
                        </span>
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">{t('admin.avatarColUsage')}</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {t('admin.avatarUsageCount', { count: avatar.usageCount })}
                      </dd>
                    </div>
                  </dl>
                  {renderActions(avatar)}
                </li>
              ))}
            </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAvatars;
